import type { IngestKey, VendorClientVersion } from "../api/types";

// Le kit envoie toutes les heures : au-delà de deux heures sans envoi accepté,
// le poste est tenu pour muet.
export const DEVICE_SILENT_AFTER_MS = 2 * 60 * 60 * 1000;

export type DeviceHealth = "ok" | "outdated" | "silent";

export interface DeviceStatus {
  health: DeviceHealth;
  label: string;
}

export const DEVICE_HEALTH_META: Record<
  DeviceHealth,
  { color: string; title: string }
> = {
  ok: { color: "var(--success)", title: "À jour" },
  outdated: { color: "var(--warning)", title: "Version en retard" },
  silent: {
    color: "var(--danger)",
    title: "Aucun envoi accepté depuis plus de 2 h",
  },
};

const HEALTH_RANK: Record<DeviceHealth, number> = {
  ok: 0,
  outdated: 1,
  silent: 2,
};

export function worseHealth(a: DeviceHealth, b: DeviceHealth): DeviceHealth {
  return HEALTH_RANK[b] > HEALTH_RANK[a] ? b : a;
}

// Seuls les postes du kit (ed25519, liés à un salarié) envoient toutes les
// heures : la règle des 2 h ferait virer au rouge une badgeuse chaque nuit.
export function isKitDevice(k: IngestKey): boolean {
  return k.scheme === "ed25519" && !!k.worker_url;
}

// État d'un poste du registre, ou null s'il ne se juge pas (révoqué, ou pas
// un poste du kit).
export function ingestKeyStatus(
  k: IngestKey,
  clientVersions: Record<string, VendorClientVersion> | undefined,
  now: Date,
): DeviceStatus | null {
  if (k.status !== "active" || !isKitDevice(k)) return null;
  return deviceStatus(
    k.last_client_version,
    k.last_seen_at,
    clientVersions?.[k.vendor],
    now,
  );
}

function parseVersion(v: string): [number, number, number] | null {
  const m = /^v?(\d+)\.(\d+)\.(\d+)$/.exec(v.trim());
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

// Vrai si `v` est au moins `ref`. Une version illisible n'est jamais à jour.
export function versionAtLeast(v: string, ref: string): boolean {
  const a = parseVersion(v);
  const b = parseVersion(ref);
  if (!a || !b) return false;
  const [a0, a1, a2] = a;
  const [b0, b1, b2] = b;
  if (a0 !== b0) return a0 > b0;
  if (a1 !== b1) return a1 > b1;
  return a2 >= b2;
}

export function formatSeenAgo(seen: Date, now: Date): string {
  const min = Math.max(0, Math.floor((now.getTime() - seen.getTime()) / 60000));
  if (min < 1) return "vu à l'instant";
  if (min < 60) return `vu il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vu il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `vu il y a ${d} j`;
}

// État d'un poste actif : muet (rouge) prime sur en retard (orange). La
// référence est la dernière version publiée, à défaut la version minimale ;
// sans référence, seul le silence se juge.
export function deviceStatus(
  lastClientVersion: string | null | undefined,
  lastSeenAt: string | null | undefined,
  ref: VendorClientVersion | undefined,
  now: Date,
): DeviceStatus {
  const version = lastClientVersion
    ? `version ${lastClientVersion}`
    : "version inconnue";
  if (!lastSeenAt) return { health: "silent", label: "Rien reçu" };
  const seen = new Date(lastSeenAt);
  const label = `${version} · ${formatSeenAgo(seen, now)}`;
  if (now.getTime() - seen.getTime() > DEVICE_SILENT_AFTER_MS) {
    return { health: "silent", label };
  }
  const target = ref?.latest || ref?.minimum;
  if (target && !versionAtLeast(lastClientVersion ?? "", target)) {
    return { health: "outdated", label: `${label} · attendue ${target}` };
  }
  return { health: "ok", label };
}
